import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FileText, Package, ChevronDown } from "lucide-react";

export default function MyOrders() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("userToken");

    if (!token) {
      navigate("/login");
      return;
    }

    async function loadOrders() {
      try {
        const res = await axios.get(`${API_BASE}/api/orders/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setOrders(res.data || []);
      } catch (err) {
        console.error("My orders error:", err);

        if (err.response?.status === 401) {
          localStorage.removeItem("userToken");
          navigate("/login");
        } else {
          setError("Could not load your orders.");
        }
      } finally {
        setLoading(false);
      }
    }

    loadOrders();
  }, [API_BASE, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 pt-24">
        Loading your orders...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="text-3xl font-semibold mb-6">My Orders</h1>

        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {orders.length === 0 ? (
          <p className="text-sm text-gray-600">You don’t have any orders yet.</p>
        ) : (
          <div className="space-y-5">
            {orders.map((order) => {
              const isExpanded = expandedOrder === order._id;

              return (
                <div
                  key={order._id}
                  className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
                >
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-800 text-lg">
                        Order #{order._id.slice(-6)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {new Date(order.createdAt).toLocaleString()}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        ${(order.amount / 100).toFixed(2)}
                      </div>
                      <div className="text-xs mt-0.5 capitalize px-2 py-1 inline-block bg-gray-200 rounded-md text-gray-700">
                        {order.status}
                      </div>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() =>
                      setExpandedOrder(isExpanded ? null : order._id)
                    }
                    className="mt-4 w-full flex items-center justify-between text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <span className="flex items-center gap-2">
                      <Package size={16} /> View Items
                    </span>

                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Items */}
                  {isExpanded && (
                    <div className="mt-4 border-t border-gray-100 pt-3 space-y-2">
                      {order.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-700"
                        >
                          <span>
                            {item.name}
                            {item.variantName && (
                              <span className="text-gray-400">
                                {" "}
                                ({item.variantName})
                              </span>
                            )}
                          </span>

                          <span>
                            x{item.qty} — ${item.price.toFixed(2)}
                          </span>
                        </div>
                      ))}

                      {/* Invoice button */}
                      <a
                        href={`${API_BASE}/api/orders/invoice/${order._id}`}
                        target="_blank"
                        className="mt-4 inline-flex items-center gap-2 text-blue-600 underline text-sm"
                      >
                        <FileText size={16} />
                        Download Invoice (PDF)
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
